import { useRef, type KeyboardEvent } from "react";
import { ContactTagBadge } from "./ContactTagBadge";
import { inPalette, selectorOrder } from "./tagColor";

// Formulario de Nueva y Editar etiqueta (Etiquetas R1 · I2): Nombre, Color (selector C45), Descripción y «Así se verá».
// La ventana (escritorio) o la pantalla completa (celular) la pone quien lo usa; aquí solo van los campos.

export type TagDraft = { name: string; color: string | null; description: string };

/**
 * Selector de color C45: una celda de 44 × 44 por color con su círculo de 28 px, en el orden del selector. Es un grupo de
 * radios: Tab entra en el elegido, las flechas mueven y eligen, Inicio y Fin van al primero y al último.
 */
export function TagColorPicker({
  value,
  label,
  name,
  onChange,
}: {
  value: string | null;
  label: string;
  name: (key: string) => string;
  onChange: (fill: string) => void;
}) {
  const chosen = inPalette(value)?.key ?? null;
  const group = useRef<HTMLDivElement>(null);
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const current = Math.max(
      0,
      selectorOrder.findIndex((item) => item.key === chosen),
    );
    let next: number | null = null;
    if (event.key in keys) next = (current + keys[event.key]! + selectorOrder.length) % selectorOrder.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = selectorOrder.length - 1;
    if (next === null) return;
    event.preventDefault();
    onChange(selectorOrder[next]!.fill);
    requestAnimationFrame(() => group.current?.querySelectorAll<HTMLElement>("[role=radio]")[next!]?.focus());
  };
  return (
    <div className="tg-colors" role="radiogroup" aria-label={label} ref={group} onKeyDown={move} data-part="selector">
      {selectorOrder.map((item, index) => {
        const selected = item.key === chosen;
        return (
          <button
            key={item.key}
            type="button"
            role="radio"
            className="tg-color"
            aria-checked={selected ? "true" : "false"}
            aria-label={name(item.label)}
            title={name(item.label)}
            tabIndex={selected || (chosen === null && index === 0) ? 0 : -1}
            onClick={() => onChange(item.fill)}
          >
            <span className="tg-swatch" style={{ background: item.fill }}>
              {selected ? <span className="tg-swatch-dot" style={{ background: item.text }}></span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Los campos del formulario; el error del nombre reemplaza a su ayuda y se va al escribir un carácter visible. */
export function TagEditorFields({
  draft,
  nameError,
  serverError,
  L,
  onChange,
  nameRef,
}: {
  draft: TagDraft;
  nameError: string | null;
  serverError: string | null;
  L: (key: string) => string;
  onChange: (draft: TagDraft) => void;
  nameRef: React.RefObject<HTMLInputElement | null>;
}) {
  const preview = draft.name.trim() ? draft.name.trim() : L("NEW");
  return (
    <div className="tg-form">
      <div className="tg-field" data-part="nombre">
        <label htmlFor="tg-name">{L("NAME")}</label>
        <input
          id="tg-name"
          ref={nameRef}
          className={nameError ? "tg-input invalid" : "tg-input"}
          value={draft.name}
          placeholder={L("R1_NAME_PLACEHOLDER")}
          maxLength={50}
          autoComplete="off"
          aria-invalid={nameError ? "true" : "false"}
          aria-describedby="tg-name-help"
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />
        <span id="tg-name-help" className={nameError ? "tg-help tg-error" : "tg-help"} role={nameError ? "alert" : undefined}>
          {nameError ?? L("NAME_HELP")}
        </span>
        {serverError ? (
          <span className="tg-help tg-error" role="alert">
            {serverError}
          </span>
        ) : null}
      </div>
      <div className="tg-field" data-part="color">
        <span className="tg-label" id="tg-color-label">
          {L("COLOR")}
        </span>
        <TagColorPicker
          value={draft.color}
          label={L("COLOR")}
          name={(key) => L(key)}
          onChange={(fill) => onChange({ ...draft, color: fill })}
        />
      </div>
      <div className="tg-field" data-part="descripcion">
        <label htmlFor="tg-description">
          {L("DESCRIPTION")} <span className="tg-optional">{L("R1_OPTIONAL")}</span>
        </label>
        <textarea
          id="tg-description"
          className="tg-input tg-textarea"
          value={draft.description}
          placeholder={L("R1_DESCRIPTION_PLACEHOLDER")}
          maxLength={200}
          aria-describedby="tg-description-help"
          onChange={(event) => onChange({ ...draft, description: event.target.value })}
        ></textarea>
        <span id="tg-description-help" className="tg-help">
          {L("DESCRIPTION_HELP")}
        </span>
      </div>
      <div className="tg-preview" data-part="vista-previa">
        <span className="tg-label">{L("PREVIEW_TAG")}</span>
        <ContactTagBadge name={preview} color={draft.color} />
      </div>
    </div>
  );
}
